/**
 * Convex-backed notification inbox.
 *
 * Replaces the `/api/notifications` REST calls and the 60-second poll that
 * stood in for them. The store and the notification centre keep working against
 * the snake_case `AppNotification` shape, so the swap is confined to this file.
 */
import { api } from '@/convex/_generated/api'
import type { Doc, Id } from '@/convex/_generated/dataModel'
import { convex, whenConvexReady } from '@/lib/convex-client'
import type { AppNotification, NotificationPriority, NotificationType } from '@/types/notification'

type ConvexNotification = Doc<'notifications'>

export function toAppNotification(doc: ConvexNotification): AppNotification {
  return {
    id: doc._id,
    user_id: doc.clerkUserId,
    type: doc.type as NotificationType,
    title: doc.title,
    message: doc.message,
    priority: doc.priority as NotificationPriority,
    read: doc.read,
    task_id: doc.taskId ?? null,
    deep_link: doc.deepLink ?? null,
    metadata: doc.metadata ?? undefined,
    created_at: new Date(doc._creationTime).toISOString(),
    read_at: doc.readAt ? new Date(doc.readAt).toISOString() : null,
  }
}

export async function createNotification(input: {
  type: NotificationType
  title: string
  message: string
  priority: NotificationPriority
  taskId?: string | null
}): Promise<AppNotification> {
  await whenConvexReady()

  const doc = await convex.mutation(api.notifications.create, {
    type: input.type,
    title: input.title,
    message: input.message,
    priority: input.priority,
    taskId: (input.taskId as Id<'tasks'> | null) ?? undefined,
  })

  return toAppNotification(doc)
}

export async function markNotificationRead(notificationId: string): Promise<void> {
  await whenConvexReady()
  await convex.mutation(api.notifications.markRead, {
    notificationId: notificationId as Id<'notifications'>,
  })
}

export async function markAllNotificationsRead(): Promise<void> {
  await whenConvexReady()
  await convex.mutation(api.notifications.markAllRead, {})
}

export async function deleteNotification(notificationId: string): Promise<void> {
  await whenConvexReady()
  await convex.mutation(api.notifications.remove, {
    notificationId: notificationId as Id<'notifications'>,
  })
}

export async function clearNotifications(): Promise<void> {
  await whenConvexReady()
  await convex.mutation(api.notifications.clearAll, {})
}

/**
 * Live inbox subscription.
 *
 * Server-produced reminders now land in the same Convex table the client reads,
 * so they arrive by subscription — there is no longer a producer on the other
 * side of a second database to poll for.
 */
export function subscribeToNotifications(
  onNotifications: (notifications: AppNotification[]) => void,
): () => void {
  let unsubscribe: (() => void) | null = null
  let cancelled = false

  void whenConvexReady().then(() => {
    if (cancelled) return
    const watch = convex.watchQuery(api.notifications.list, {})
    unsubscribe = watch.onUpdate(() => {
      const docs = watch.localQueryResult()
      if (docs) onNotifications(docs.map(toAppNotification))
    })
  })

  return () => {
    cancelled = true
    unsubscribe?.()
  }
}

/** Registers this browser's Web Push subscription against the signed-in user. */
export async function registerWebPushSubscription(input: {
  endpoint: string
  p256dh: string
  auth: string
  deviceId?: string
  userAgent?: string
}): Promise<void> {
  await whenConvexReady()
  await convex.mutation(api.pushSubscriptions.registerWebPush, input)
}
